/**
 * EnvironmentalRoom — shared environmental backdrop system.
 *
 * The Archive is one secure intelligence facility; each major area is a
 * different room inside it. This component renders layers 2–4 of the
 * locked visual stack:
 *
 *   1. Base black background            (owned by the host section)
 *   2. Environmental room image         (this component)
 *   3. Grayscale/sepia/contrast filter  (this component)
 *   4. Dark vignette + readability      (this component)
 *   5. Red ambient glow / spill         (owned by the host — NEVER flattened here)
 *   6. Interface content                (owned by the host)
 *
 * Rules: decorative only (aria-hidden, empty alt), static (no animation,
 * reduced-motion safe), subordinate to the interface (~5–8 % perceived
 * visibility), and never contains information.
 *
 * ROOM IDENTITIES
 *   reception — Homepage. Entrance / intelligence reception desk.
 *   archive   — /archive Research Table. ALREADY IMPLEMENTED directly in
 *               ArchiveHero.jsx (verified & locked). Do not migrate.
 *   members   — FUTURE: Private Operations Office (clean personal
 *               workstation, secure monitor glow, cipher notebook).
 *               Not yet designed — intentionally absent from ROOMS.
 *   oracle    — FUTURE: Deep Archive Chamber (predominantly black, faint
 *               shelving disappearing into darkness). Not yet designed.
 */
import React from "react";

export const ROOMS = {
  reception: {
    src: "/img/rooms/reception-desk-1264.webp",
    srcSet: "/img/rooms/reception-desk-768.webp 768w, /img/rooms/reception-desk-1264.webp 1264w",
    sizes: "100vw",
    opacity: 0.28,
    filter: "grayscale(38%) sepia(10%) brightness(0.95) contrast(0.98)",
    // Desktop: desk detail lives in the outer thirds; centre stays quiet.
    // Mobile: intentional re-crop toward the lamp/desk, not a plain shrink.
    positionClass: "object-[68%_38%] md:object-[center_42%]",
    overlay:
      "radial-gradient(ellipse 130% 95% at 50% 40%, transparent 0%, rgba(11,10,13,0.35) 60%, rgba(11,10,13,0.75) 100%)",
  },
};

export const EnvironmentalRoom = ({ room }) => {
  const cfg = ROOMS[room];
  if (!cfg) return null;
  return (
    <>
      <img
        src={cfg.src}
        srcSet={cfg.srcSet}
        sizes={cfg.sizes}
        alt=""
        aria-hidden="true"
        loading="eager"
        decoding="async"
        draggable={false}
        className={`absolute inset-0 -z-10 w-full h-full object-cover pointer-events-none select-none ${cfg.positionClass}`}
        style={{ opacity: cfg.opacity, filter: cfg.filter }}
        data-testid={`environmental-room-${room}`}
      />
      {/* Vignette + readability gradients (layer 4) */}
      <div
        aria-hidden="true"
        className="absolute inset-0 -z-10 pointer-events-none"
        style={{ background: cfg.overlay }}
      />
      {/* Mobile: keep the text zone nearly black — extra darkness < md */}
      <div
        aria-hidden="true"
        className="absolute inset-0 -z-10 pointer-events-none md:hidden"
        style={{ background: "rgba(11,10,13,0.35)" }}
      />
    </>
  );
};

export default EnvironmentalRoom;
